import { useCallback, useEffect, useRef, useState } from 'react';

interface EditableTitleProps {
  title: string;
  onSave: (newTitle: string) => Promise<void>;
}

export default function EditableTitle({ title, onSave }: EditableTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDraft(title);
  }, [title]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const save = useCallback(async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === title) {
      setEditing(false);
      setDraft(title);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
    } catch {
      // silently fail — title stays as-is
    } finally {
      setSaving(false);
      setEditing(false);
    }
  }, [draft, title, onSave]);

  const cancel = useCallback(() => {
    setDraft(title);
    setEditing(false);
  }, [title]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        save();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    },
    [save, cancel],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (containerRef.current?.contains(e.relatedTarget as Node)) {
        return;
      }
      save();
    },
    [save],
  );

  if (!editing) {
    return (
      <h1
        role="button"
        tabIndex={0}
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setEditing(true);
          }
        }}
        style={{
          fontSize: '22px',
          fontWeight: 600,
          margin: '8px 0 4px',
          cursor: 'pointer',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '600px',
        }}
      >
        {title}
      </h1>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', margin: '8px 0 4px' }}
      onBlur={handleBlur}
    >
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={saving}
        style={{
          fontSize: '22px',
          fontWeight: 600,
          padding: '2px 8px',
          border: '1px solid #ccc',
          borderRadius: '6px',
          outline: 'none',
          width: '600px',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      />
      <button
        type="button"
        className="btn-small"
        disabled={saving}
        onClick={save}
        style={{ whiteSpace: 'nowrap' }}
      >
        Save
      </button>
      <button
        type="button"
        className="btn-small btn-secondary"
        disabled={saving}
        onClick={cancel}
        style={{ whiteSpace: 'nowrap' }}
      >
        Cancel
      </button>
    </div>
  );
}
